import { Divider, Stack } from '@mui/material'
import { CaretDoubleDownIcon, CaretDoubleUpIcon, HandshakeIcon } from '@phosphor-icons/react'
import { isoDateStringToLocalFormat, tokens, User } from 'ComponentLibrary'
import { IconButton } from 'ComponentLibrary/src/piazza'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { useState } from 'react'
import { ApprovalStatusAction, HandoverStatusAction } from './HandoverApprovalStatusActions'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type HandoverApprovalSectionProps = {
    isHandedOver?: boolean
    handedOverOn?: string
    handedOverBy?: string
    isApproved?: boolean
    approvedOn?: string
    approvedBy?: string
    isInternallyApproved?: boolean
    internallyApprovedOn?: string
    internallyApprovedBy?: string
    showInternalApproval?: boolean
    users: User[]
    hasAnnotationsToHandover: boolean
    hasNewerDraft?: boolean
    isValuationPlausibilityPending?: boolean
    onOpenHandoverModal: () => void
    onOpenApprovalModal: () => void
    onOpenInternalApprovalModal: () => void
}

export function HandoverApprovalSection({
    isHandedOver,
    handedOverOn,
    handedOverBy,
    isApproved,
    approvedOn,
    approvedBy,
    isInternallyApproved,
    internallyApprovedOn,
    internallyApprovedBy,
    showInternalApproval,
    users,
    hasAnnotationsToHandover,
    hasNewerDraft,
    isValuationPlausibilityPending,
    onOpenHandoverModal,
    onOpenApprovalModal,
    onOpenInternalApprovalModal,
}: HandoverApprovalSectionProps) {
    const { t: tCommonActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tPlausibility } = useTranslation('terminology', { keyPrefix: 'plausibility' })
    const { t: tHandoverApprovalSection } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.handoverApprovalSection',
    })
    const locale = useCurrentResolvedLocale()
    const [isOpen, setIsOpen] = useState(true)

    const handedOverByUser = users.find((user) => user.uuid === handedOverBy)
    const approvedByUser = users.find((user) => user.uuid === approvedBy)
    const internallyApprovedByUser = users.find((user) => user.uuid === internallyApprovedBy)

    const formattedHandoverDate = isoDateStringToLocalFormat(handedOverOn, false, locale)
    const formattedApprovalDate = isoDateStringToLocalFormat(approvedOn, false, locale)
    const formattedInternalApprovalDate = isoDateStringToLocalFormat(internallyApprovedOn, false, locale)

    const olderDraftTooltip = tHandoverApprovalSection('olderDraftTooltip')
    const noAnnotationsTooltip = tPlausibility('noAnnotations')

    const handoverTooltip = hasNewerDraft ? olderDraftTooltip : !hasAnnotationsToHandover ? noAnnotationsTooltip : undefined
    const isHandoverDisabled = !!handoverTooltip
    const approvalTooltip = hasNewerDraft ? olderDraftTooltip : undefined
    const isApprovalDisabled = !!approvalTooltip

    return (
        <Stack direction="column" width="100%" minWidth={0}>
            <Stack
                direction="row"
                height="56px"
                alignItems="center"
                borderTop={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
                paddingX={tokens.SizingSpacing200}
                gap={tokens.SizingSpacing100}
            >
                <HandshakeIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} style={{ flexShrink: 0 }} />
                <Headline variant="headline3" title={tHandoverApprovalSection('title')} fullWidth />
                <IconButton
                    size="small"
                    variant="tertiary"
                    icon={isOpen ? <CaretDoubleDownIcon /> : <CaretDoubleUpIcon />}
                    onClick={() => setIsOpen((previous) => !previous)}
                    tooltip={isOpen ? tCommonActions('collapse') : tCommonActions('expand')}
                />
            </Stack>

            {isOpen && (
                <Stack
                    direction="column"
                    borderTop={`2px solid ${tokens.ColorBorderDefault}`}
                    padding={tokens.SizingSpacing200}
                    gap={tokens.SizingSpacing150}
                    minWidth={0}
                >
                    <HandoverStatusAction
                        isValuationPlausibilityPending={isValuationPlausibilityPending}
                        isHandedOver={isHandedOver}
                        isApproved={isApproved}
                        isInternallyApproved={isInternallyApproved}
                        handedOverByUser={handedOverByUser}
                        approvedByUser={approvedByUser}
                        internallyApprovedByUser={internallyApprovedByUser}
                        formattedHandoverDate={formattedHandoverDate}
                        formattedApprovalDate={formattedApprovalDate}
                        formattedInternalApprovalDate={formattedInternalApprovalDate}
                        handoverTooltip={handoverTooltip}
                        isHandoverDisabled={isHandoverDisabled}
                        onOpenHandoverModal={onOpenHandoverModal}
                        handoverInfoText={tHandoverApprovalSection('handoverInfoText')}
                    />

                    <Divider sx={{ width: '100%', borderColor: tokens.ColorBorderDefault }} />

                    {showInternalApproval && (
                        <>
                            <ApprovalStatusAction
                                isValuationPlausibilityPending={isValuationPlausibilityPending}
                                isApproved={isInternallyApproved}
                                approvedByUser={internallyApprovedByUser}
                                formattedApprovalDate={formattedInternalApprovalDate}
                                approvalTooltip={approvalTooltip}
                                isApprovalDisabled={isApprovalDisabled}
                                onOpenApprovalModal={onOpenInternalApprovalModal}
                                approvedChipLabel={tPlausibility('draftApproved')}
                                approvedTooltipLabel={tPlausibility('approvedBy')}
                                openApprovalDescription={tHandoverApprovalSection('internalApproval.openApprovalDescription')}
                                approvedInfoText={isApproved ? undefined : tHandoverApprovalSection('internalApproval.approvedInfoText')}
                                sectionLabel={tHandoverApprovalSection('internalApproval.sectionLabel')}
                            />
                            <Divider sx={{ width: '100%', borderColor: tokens.ColorBorderDefault }} />
                        </>
                    )}

                    <ApprovalStatusAction
                        isValuationPlausibilityPending={isValuationPlausibilityPending}
                        isApproved={isApproved}
                        approvedByUser={approvedByUser}
                        formattedApprovalDate={formattedApprovalDate}
                        approvalTooltip={approvalTooltip}
                        isApprovalDisabled={isApprovalDisabled}
                        onOpenApprovalModal={onOpenApprovalModal}
                        sectionLabel={showInternalApproval ? tHandoverApprovalSection('approval.sectionLabel') : undefined}
                        approvedInfoText={tHandoverApprovalSection('approval.approvedInfoText')}
                    />
                </Stack>
            )}
        </Stack>
    )
}
